import Button from "@ui/button/Button"
import { useEffect, useState } from "react"
import Config from "src/Config"
import { superAdminFetch } from "src/services/superAdminFetch"
import { systemErrorMessage } from "src/services/superAdminApi"
import { T } from "../adminTheme"
import { Page, Panel, State, StatusBadge, Table } from "./adminUi"

interface SystemJob {
	key: string
	env: string
	enabled: boolean
	label: string
	interval_ms: number | null
}

export const jobInterval = (ms: number | null) => {
	if (ms === null) return "On startup / queue"
	if (!Number.isFinite(ms) || ms <= 0) return "Unknown interval"
	for (const [unit, size] of [["d", 86400000], ["h", 3600000], ["min", 60000], ["s", 1000]] as const) {
		if (ms >= size && ms % size === 0) return `Every ${ms / size} ${unit}`
	}
	return `Every ${ms} ms`
}

export const SuperAdminJobs = ({ token }: { token: string }) => {
	const [jobs, setJobs] = useState<SystemJob[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState("")
	const [revision, setRevision] = useState(0)
	useEffect(() => {
		let active = true
		setLoading(true)
		setError("")
		void (async () => {
			try {
				const response = await superAdminFetch(Config.scheme.URL_API + "system/jobs", { method: "GET", headers: { Authorization: `Bearer ${token}` } })
				const payload = await response.json()
				if (payload.error) throw new Error(systemErrorMessage(payload.error))
				if (!response.ok) throw new Error(response.statusText || "Failed to load jobs")
				if (!Array.isArray(payload.data)) throw new Error("Invalid jobs response")
				if (active) setJobs(payload.data)
			} catch (cause) {
				if (active) { setJobs([]); setError(cause instanceof Error ? cause.message : "Failed to load jobs") }
			} finally { if (active) setLoading(false) }
		})()
		return () => { active = false }
	}, [token, revision])
	return <Page>
		<Panel title='Background jobs' hint='Scheduled and queued tasks on this instance.' actions={<Button variant='secondarySmall' disabled={loading} onClick={() => setRevision(value => value + 1)}>Refresh jobs</Button>}>
			<State loading={loading} error={error} empty={!loading && !error && jobs.length === 0} emptyText='No background jobs reported.' />
			{!loading && !error && jobs.length > 0 && <Table>
				<thead><tr><th>Task</th><th>Status</th><th>Interval</th></tr></thead>
				<tbody>{jobs.map(job => <tr key={job.key}>
					<td><span style={{ fontWeight: 550 }}>{job.label || job.key}</span></td>
					<td><StatusBadge value={job.enabled ? "Enabled" : "Disabled"} /></td>
					<td style={{ whiteSpace: "nowrap", color: T.text2 }}>{jobInterval(job.interval_ms)}</td>
				</tr>)}</tbody>
			</Table>}
		</Panel>
	</Page>
}
